import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Segmented,
  Select,
  Space,
  Checkbox,
  Switch,
} from "antd";
import GenericCard from "components/UI/GenericCard/GenericCard";
import { modelSearchRangePresets } from "utils/constants";
import dayjs from "dayjs";

import IndicatorsSelector from "./IndicatorsSelector";
import { useWatch } from "antd/es/form/Form";
import { apiEvaluateBuySellPolarity } from "api/apiPredictions";
import BuyTotalStocksCard from "./BuyTotalStocksCard";

const { RangePicker } = DatePicker;

// Set when the model type changes. Random Forest: more trees only stabilise it (gains stop ~500),
// depth 4-6 for noisy price data. XGBoost (learning rate 0.3): trees add up, so fewer and shallower.
const MODEL_DEFAULTS = {
  rfc: { n_estimators: 500, max_depth: 5 },
  xgboost: { n_estimators: 200, max_depth: 3 },
};

const CreateModelForm = (props) => {
  const { form, isPending, isPredicting, symbolsList, handleCreateModel } =
    props;

  const [symbolsString, setSymbolsString] = useState("");
  const [evaluation, setEvaluation] = useState("");
  const [totalCount, setTotalCount] = useState(null);
  const [buyTotalCount, setBuyTotalCount] = useState(null);
  const [buyTotalResults, setBuyTotalResults] = useState(null);
  const resolution = useWatch("resolution", form);

  const searchSymbol = (query, option) => {
    if (option.options) return false;
    return (option?.label ?? "")
      .toLowerCase()
      .includes((query ?? "").toLowerCase());
  };

  const handleSelectAll = (type) => {
    let symbols = [];
    switch (type) {
      case "tech":
        symbols = symbolsList.reduce((acc, item) => {
          if (item.sector.code === "technology") acc.push(item.ticker);
          return acc;
        }, []);
        break;

      default:
        break;
    }

    form.setFieldValue("leads", symbols);
  };

  const handleAddSymbolsString = () => {
    let symbols = symbolsString
      .split(",")
      .map((symbol) => symbol.trim().toUpperCase());
    symbols = symbols.filter((symbol) =>
      symbolsList.find((s) => s.ticker === symbol)
    );
    form.setFieldValue("leads", symbols);
    setSymbolsString("");
  };

  const symbolsGroupedInSectored = useMemo(() => {
    const sectors = symbolsList.reduce((acc, item) => {
      if (!item.candles[resolution]?.hasData) return acc;
      const symbol = {
        label: `${item.name} - (${item.ticker}) ${
          buyTotalResults && buyTotalResults[item.ticker]?.polarity
            ? buyTotalResults[item.ticker]?.polarity + "%"
            : ""
        }`,
        value: item.ticker,
      };
      acc[item.sector.name] = acc[item.sector.name]
        ? [...acc[item.sector.name], symbol]
        : [symbol];
      return acc;
    }, {});

    const result = Object.entries(sectors).map((sector) => {
      return {
        label: sector[0],
        options: sector[1],
      };
    });
    return result;
  }, [symbolsList, buyTotalResults, resolution]);

  // Prevent Enter key from submitting the form
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Allow Enter for textarea and buttons
      if (
        e.target.tagName !== "TEXTAREA" &&
        e.target.type !== "submit" &&
        e.target.type !== "button"
      ) {
        e.preventDefault();
      }
    }
  };

  const evaluateBuySellPolarity = async () => {
    const { dates, days, percToGain, resolution } = form.getFieldsValue();
    const response = await apiEvaluateBuySellPolarity({
      startDate: dates[0].format("YYYY-MM-DD"),
      endDate: dates[1].format("YYYY-MM-DD"),
      bars: days,
      percToGain,
      resolution,
    });

    setBuyTotalResults(response);
  };

  const evaluateTotalPolarity = () => {
    const { leads } = form.getFieldsValue();
    if (!buyTotalResults || !leads) return;
    const results = leads.reduce(
      (acc, value) => {
        acc.allTrue += buyTotalResults[value]?.trueCount || 0;
        acc.allTotal += buyTotalResults[value]?.total || 0;
        return acc;
      },
      { allTrue: 0, allTotal: 0 }
    );
    const allTruePercentage = (results.allTrue / results.allTotal) * 100;
    setEvaluation(allTruePercentage);
    setTotalCount(results.allTotal);
    setBuyTotalCount(results.allTrue);
  };

  // Test range follows the training end date: day after it -> today (all data the model never saw).
  // Empty when training ends today (no test period). Still editable by hand.
  const handleValuesChange = (changedValues) => {
    if ("modelType" in changedValues) {
      form.setFieldsValue(MODEL_DEFAULTS[changedValues.modelType]);
      return;
    }
    if (!("dates" in changedValues)) return;
    const testStart = changedValues.dates?.[1]?.add(1, "day");
    form.setFields([
      {
        name: "testDates",
        value: testStart && !testStart.isAfter(dayjs(), "day") ? [testStart, dayjs()] : null,
        errors: [],
      },
    ]);
  };

  useEffect(() => {
    if (buyTotalResults) {
      evaluateTotalPolarity();
    }
  }, [buyTotalResults]);

  return (
    <GenericCard maxHeight={1000}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          percToGain: 5,
          days: 14,
          rsi: 14,
          obv: 9,
          // train 2 years, test on the last year
          dates: [dayjs().add(-3, "y"), dayjs().add(-1, "y")],
          testDates: [dayjs().add(-1, "y").add(1, "day"), dayjs()],
          walkForward: false,
          wfTrainYears: 5,
          // last year at most: the current year stays out as the final untouched check
          wfTestYears: [dayjs("2019-01-01"), dayjs().add(-1, "y")],
          indicators: {},
          modelType: "rfc",
          resolution: "1d",
          ...MODEL_DEFAULTS.rfc,
        }}
        onFieldsChange={evaluateTotalPolarity}
        onValuesChange={handleValuesChange}
        onFinish={handleCreateModel}
        onKeyDown={handleKeyDown}
      >
        <div className="flex gap-6">
          <Form.Item name="modelType" label="Model type">
            <Segmented
              options={[
                { label: "Random Forest", value: "rfc" },
                { label: "XG Boost", value: "xgboost" },
              ]}
            />
          </Form.Item>
          <Form.Item name="resolution" label="Resolution">
            <Segmented
              options={[
                { label: "1D", value: "1d" },
                { label: "1H", value: "1h" },
              ]}
            />
          </Form.Item>
        </div>

        <div className="flex flex-col xl:gap-20 xl:flex-row">
          <div className="basis-[40%]">
            <Form.Item
              name="name"
              label="Model name"
              required={false}
              rules={[
                {
                  required: true,
                  message: "",
                },
              ]}
            >
              <Input />
            </Form.Item>
            <div className="flex gap-4">
              <Form.Item
                name="max_depth"
                label="Max depth"
                max={20}
                min={1}
                required={false}
                rules={[
                  {
                    required: true,
                    message: "",
                  },
                ]}
              >
                <InputNumber />
              </Form.Item>
              <Form.Item
                name="n_estimators"
                label="Number of estimators"
                max={10000}
                min={1}
                required={false}
                rules={[
                  {
                    required: true,
                    message: "",
                  },
                ]}
              >
                <InputNumber />
              </Form.Item>
            </div>
            <Form.Item
              name="walkForward"
              valuePropName="checked"
              tooltip="One model per test year, each trained on the years before it (rolling window). Shows stats per year and saves out-of-sample predictions for every test year. No live model is saved."
              label="Walk-forward"
            >
              <Switch size="small" />
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.walkForward !== cur.walkForward}>
              {({ getFieldValue }) =>
                getFieldValue("walkForward") ? (
                  <div className="flex gap-4">
                    <Form.Item
                      name="wfTrainYears"
                      label="Train years"
                      tooltip="Each fold trains on this many years before its test year"
                      rules={[{ required: true, message: "" }]}
                    >
                      <InputNumber min={1} max={20} />
                    </Form.Item>
                    <Form.Item
                      name="wfTestYears"
                      label="Test years"
                      tooltip="One fold per year. Keep the current year out as a final untouched check."
                      rules={[{ required: true, message: "" }]}
                    >
                      <RangePicker picker="year" />
                    </Form.Item>
                  </div>
                ) : (
                  <>
                    <Form.Item name={"dates"} label="Dates">
                      <RangePicker
                        presets={modelSearchRangePresets}
                        format="DD/MM/YY"
                        style={{
                          width: "100%",
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      name="testDates"
                      label="Test range"
                      tooltip="Stats are measured on this range only. Follows the end date (day after it to today); edit it by hand to re-create a model with its saved test dates. Empty = no test stats."
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, testDates) {
                            const trainEnd = getFieldValue("dates")?.[1];
                            if (!testDates?.[0] || !trainEnd || testDates[0].isAfter(trainEnd, "day")) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error("Test range must start after the training end date")
                            );
                          },
                        }),
                      ]}
                    >
                      <RangePicker
                        format="DD/MM/YY"
                        placeholder={["Day after end date", "Latest bar"]}
                        style={{
                          width: "100%",
                        }}
                      />
                    </Form.Item>
                  </>
                )
              }
            </Form.Item>

            <div className="flex gap-4">
              <Form.Item
                name="percToGain"
                label="% wanted Profit"
                required={false}
                rules={[
                  {
                    required: true,
                    message: "",
                  },
                ]}
              >
                <InputNumber />
              </Form.Item>
              <Form.Item
                name="days"
                label="Bars target"
                required={false}
                rules={[
                  {
                    required: true,
                    message: "",
                  },
                ]}
              >
                <InputNumber />
              </Form.Item>
            </div>

            <div className="pt-1 pb-2 flex gap-2">
              <Button type="dashed" onClick={evaluateBuySellPolarity}>
                Calculate Buy/Total
              </Button>
            </div>

            <Form.Item
              name="leads"
              label="Symbols"
              required={false}
              rules={[
                {
                  required: true,
                  message: "",
                },
              ]}
            >
              <Select
                mode="multiple"
                style={{
                  width: "100%",
                }}
                placeholder="Select symbols"
                options={symbolsGroupedInSectored}
                filterOption={searchSymbol}
                optionLabelProp="value"
                allowClear
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider
                      orientation="left"
                      style={{
                        margin: "8px 0",
                        fontSize: "14px",
                      }}
                    >
                      Select groups
                    </Divider>
                    <Space
                      style={{
                        padding: "0 8px 4px",
                      }}
                    >
                      <Button
                        type="text"
                        // icon={<PlusOutlined />}
                        onClick={() => handleSelectAll("tech")}
                      >
                        Tech
                      </Button>
                    </Space>
                    <Divider
                      orientation="left"
                      style={{
                        margin: "8px 0",
                        fontSize: "14px",
                      }}
                    >
                      Free text
                    </Divider>
                    <div className="flex px-4 pb-2">
                      <Input
                        placeholder="AAPL, TSLA, NVDA"
                        className="me-2"
                        onChange={(e) => setSymbolsString(e.target.value)}
                        value={symbolsString}
                      />
                      <Button
                        type="primary"
                        onClick={() => handleAddSymbolsString()}
                        disabled={!symbolsString}
                      >
                        OK
                      </Button>
                    </div>
                    <p className="px-4 pb-4 text-xs">
                      Insert a list of tickers separated by commas (e.g., AAPL,
                      TSLA, NVDA).
                    </p>
                  </>
                )}
              />
            </Form.Item>
          </div>

          <IndicatorsSelector />
        </div>
        <BuyTotalStocksCard
          evaluation={evaluation}
          totalCount={totalCount}
          buyTotalCount={buyTotalCount}
          form={form}
        />

        <div>
          <Form.Item name="evalOnly" valuePropName="checked">
            <Checkbox>Evaluate only</Checkbox>
          </Form.Item>
          <Form.Item shouldUpdate>
            {() => (
              <Button
                loading={isPending}
                disabled={isPending || isPredicting}
                type="primary"
                htmlType="submit"
              >
                Create new model
              </Button>
            )}
          </Form.Item>
        </div>
      </Form>
    </GenericCard>
  );
};

CreateModelForm.propTypes = {
  form: PropTypes.object.isRequired,
  isPending: PropTypes.bool.isRequired,
  isPredicting: PropTypes.bool.isRequired,
  symbolsList: PropTypes.array.isRequired,
  handleCreateModel: PropTypes.func.isRequired,
};

export default CreateModelForm;
